import React, { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Modal } from './ui/Modal';
import OsScroll from './ui/OsScroll';

/** What the user types to commit anyway — a deliberate act, not a click-through. */
export const COMMIT_ANYWAY_PHRASE = 'commit anyway';

/**
 * Git sync Stage 1, card 5: the secret scan found what looks like a
 * credential in files Apiboo was about to commit. Shows `file:line` and the
 * rule — never the value — and how to fix it. Pushing anyway needs the phrase
 * typed out.
 *
 * @param {{ file: string, line: number, rule: string }[]} findings
 * @param {() => void} onCommitAnyway
 */
/** A task's or document's Markdown (boards & documents) vs a workspace's YAML: the fix differs. */
const isText = (file) => String(file || '').endsWith('.md');

export default function GitSecretsModal({ isOpen, onClose, findings = [], onCommitAnyway, loading = false }) {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (!isOpen) setTyped('');
  }, [isOpen]);
  if (!isOpen) return null;

  const confirmed = typed.trim().toLowerCase() === COMMIT_ANYWAY_PHRASE;
  const inText = findings.some((f) => isText(f.file));
  const inYaml = findings.some((f) => !isText(f.file));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nothing was committed"
      subtitle={`${findings.length} place${findings.length === 1 ? '' : 's'} look${findings.length === 1 ? 's' : ''} like a password, token or key`}
      customIcon={<ShieldAlert size={22} />}
      preventBackdropClose={loading}
    >
      <div className="space-y-4" data-testid="git-secrets-modal">
        <OsScroll style={{ maxHeight: 260 }} data-testid="git-secrets-list">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {findings.map((f) => (
              <div key={`${f.file}:${f.line}:${f.rule}`} className="settings-row" style={{ alignItems: 'baseline', gap: '12px' }}>
                <span className="font-mono text-xs" style={{ wordBreak: 'break-all', flex: 1, minWidth: 0 }}>
                  {f.file}:{f.line}
                </span>
                <span className="text-xs" style={{ opacity: 0.75, flexShrink: 0, maxWidth: '45%' }}>{f.rule}</span>
              </div>
            ))}
          </div>
        </OsScroll>

        {inText && (
          <p className="text-sm" style={{ opacity: 0.85 }} data-testid="git-secrets-fix-text">
            In a task or document: open it and replace the value with a placeholder such as <span className="font-mono">{'<password>'}</span>.
            Keep the real one in the Vault.
          </p>
        )}
        {inYaml && (
          <p className="text-sm" style={{ opacity: 0.85 }} data-testid="git-secrets-fix-yaml">
            In a workspace: move the value into a variable marked <strong>secret</strong> and use <span className="font-mono">{'{{name}}'}</span> instead —
            secret values are kept in an untracked <span className="font-mono">*.local.yaml</span> file and never reach the repository.
          </p>
        )}

        <label className="block text-xs" style={{ opacity: 0.8 }}>
          To commit and push anyway, type <span className="font-mono">{COMMIT_ANYWAY_PHRASE}</span>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={loading}
            aria-label="Type commit anyway to confirm"
            className="modal-input mt-1"
          />
        </label>

        <div className="flex items-center justify-end gap-2">
          <button type="button" className="settings-action-btn" onClick={onClose} disabled={loading}>
            Fix it first
          </button>
          <button
            type="button"
            className="settings-action-btn"
            disabled={!confirmed || loading}
            onClick={() => onCommitAnyway?.()}
          >
            Commit anyway
          </button>
        </div>
      </div>
    </Modal>
  );
}
